
import React, { useState, useEffect, useRef } from 'react';
import { Merchant, MealType, SampleRecord } from '../types';

interface SamplingViewProps {
  merchants: Merchant[];
  onCancel: () => void;
  onSubmit: (record: SampleRecord) => void;
  initialMerchantId?: string;
}

const INITIAL_DISHES = ['笋丝炒肉', '红烧排骨', '清蒸鱼', '土豆牛腩', '素三鲜', '麻婆豆腐', '回锅肉', '青椒肉丝', '宫保鸡丁', '蒜蓉西蓝花'];

const SamplingView: React.FC<SamplingViewProps> = ({ merchants, onCancel, onSubmit, initialMerchantId }) => {
  const [selectedMerchantId, setSelectedMerchantId] = useState(initialMerchantId || merchants[0].id);
  const [samplerName, setSamplerName] = useState('管理员');
  const [chefName, setChefName] = useState('张大厨');
  const [mealType, setMealType] = useState<MealType>(MealType.Lunch);
  const [currentWeight, setCurrentWeight] = useState(0);
  const [searchDish, setSearchDish] = useState('');
  const [selectedDish, setSelectedDish] = useState('');
  const [sessionSamples, setSessionSamples] = useState<SampleRecord[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [customDishes, setCustomDishes] = useState<string[]>([]);
  const [showAddDishModal, setShowAddDishModal] = useState(false);
  const [newDishName, setNewDishName] = useState('');
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const todayStr = new Date().toISOString().split('T')[0];

  const allDishes = [...INITIAL_DISHES, ...customDishes];

  // 模拟称重数值跳动
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWeight(prev => {
        const target = selectedDish ? 132 : 0;
        const diff = target - prev;
        const noise = selectedDish ? (Math.random() - 0.5) * 0.4 : 0;
        return Math.max(0, prev + diff * 0.2 + noise);
      });
    }, 50);
    return () => clearInterval(interval);
  }, [selectedDish]);

  const handleStartCamera = async () => {
    setIsCapturing(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (e) {
      alert("无法启动摄像头，请检查权限。");
      setIsCapturing(false);
    }
  };

  const handleTakeSample = () => {
    if (!selectedDish) {
      alert("请先从左侧选择一个菜品");
      return;
    }
    
    const now = new Date();
    const expiry = new Date();
    expiry.setHours(expiry.getHours() + 48);
    const formatDate = (date: Date) => date.toISOString().slice(0, 19).replace('T', ' ');

    const newRecord: SampleRecord = {
      id: `r-${Date.now()}-${Math.random()}`,
      merchantId: selectedMerchantId,
      dishName: selectedDish,
      weight: Math.round(currentWeight),
      samplingTime: formatDate(now),
      expiryTime: formatDate(expiry),
      sampler: samplerName,
      chef: chefName,
      mealType,
      photoUrl: `https://picsum.photos/seed/${selectedDish}${Date.now()}/120/120`
    };

    setSessionSamples(prev => [...prev, newRecord]);
    setSelectedDish('');
    setSearchDish('');
  };

  const removeSample = (id: string) => {
    setSessionSamples(prev => prev.filter(s => s.id !== id));
  };

  const handleFinalSubmit = () => {
    if (sessionSamples.length === 0) {
      alert("请先录入留样菜品");
      return;
    }
    sessionSamples.forEach(sample => onSubmit(sample));
    onCancel();
  };

  const handleAddManualDish = () => {
    if (!newDishName.trim()) return;
    setCustomDishes(prev => [newDishName.trim(), ...prev]);
    setSelectedDish(newDishName.trim());
    setNewDishName('');
    setShowAddDishModal(false);
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 rounded-[2rem] shadow-2xl overflow-hidden border border-slate-200">
      
      {/* 顶部：极简信息栏 */}
      <div className="bg-white px-8 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center space-x-8">
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">留样日期</span>
            <span className="text-lg font-black text-slate-800 font-mono">{todayStr}</span>
          </div>
          <div className="h-8 w-px bg-slate-100"></div>
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">留样人</span>
            <span className="text-lg font-black text-blue-600">{samplerName}</span>
          </div>
          <div className="h-8 w-px bg-slate-100"></div>
          
          <div className="flex items-center space-x-3">
            <div className="bg-blue-50 px-4 py-1.5 rounded-xl border border-blue-100">
               <select 
                value={selectedMerchantId}
                onChange={e => setSelectedMerchantId(e.target.value)}
                className="bg-transparent text-sm font-black text-blue-800 outline-none appearance-none"
               >
                 {merchants.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
               </select>
            </div>
            <div className="bg-blue-50 px-4 py-1.5 rounded-xl border border-blue-100">
               <select 
                value={mealType}
                onChange={e => setMealType(e.target.value as MealType)}
                className="bg-transparent text-sm font-black text-blue-800 outline-none appearance-none"
               >
                 {Object.values(MealType).map(type => <option key={type} value={type}>{type}</option>)}
               </select>
            </div>
          </div>
        </div>

        {/* 右上角：修正为留样人标识 */}
        <div className="flex items-center space-x-3">
          <div className="text-right">
            <p className="text-[10px] font-black text-slate-400 uppercase">当前留样人</p>
            <p className="text-sm font-black text-slate-800">{samplerName}</p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-200 overflow-hidden shadow-sm">
            <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=sampler-v5" className="w-full h-full object-cover" alt="sampler" />
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        
        {/* 左侧：菜品选择列表 */}
        <div className="w-[22%] bg-white border-r border-slate-100 flex flex-col">
          <div className="p-4 border-b border-slate-50 bg-slate-50/50">
            <input 
              placeholder="搜索菜品..."
              value={searchDish}
              onChange={e => setSearchDish(e.target.value)}
              className="w-full bg-white border border-slate-200 px-4 py-2.5 rounded-xl text-sm font-bold shadow-sm outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div className="flex-1 overflow-y-auto no-scrollbar p-2 space-y-1">
            {allDishes.filter(d => d.includes(searchDish)).map((dish) => (
              <button 
                key={dish}
                onClick={() => setSelectedDish(dish)}
                className={`w-full text-left px-5 py-4 rounded-xl text-base font-black transition-all truncate ${
                  selectedDish === dish 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'text-slate-600 hover:bg-slate-50 active:bg-slate-100'
                }`}
              >
                {dish}
              </button>
            ))}
          </div>
          <div className="p-4 border-t border-slate-100">
            <button 
              onClick={() => setShowAddDishModal(true)}
              className="w-full py-3 bg-white border border-slate-200 rounded-xl text-slate-400 font-black text-xs shadow-sm active:scale-95 transition-all"
            >
              手工增加菜品 +
            </button>
          </div>
        </div>

        {/* 中间：称重状态区 */}
        <div className="w-[30%] flex flex-col p-6 space-y-6 bg-slate-50/50">
          <div className="flex-1 bg-white rounded-[2.5rem] shadow-sm border border-slate-200/50 flex flex-col items-center justify-center relative">
            <span className="absolute top-8 text-[10px] font-black text-slate-300 tracking-[0.5em] uppercase">实时称重状态</span>
            <div className="flex items-baseline text-blue-700">
              <span className="text-8xl font-mono font-black tracking-tighter">{Math.round(currentWeight)}</span>
              <span className="text-2xl font-black ml-2 opacity-30">g</span>
            </div>
            <p className="mt-8 text-xl font-black text-slate-800 truncate px-4 text-center w-full">
              {selectedDish || '请先在左侧选择菜品'}
            </p>
          </div>
          <div className="h-[30%] bg-blue-700 rounded-[2.5rem] p-8 flex flex-col items-center justify-center text-center shadow-lg relative overflow-hidden">
             <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
             <p className="text-white text-xl font-black tracking-widest relative z-10">保障安全 · 溯源有据</p>
          </div>
        </div>

        {/* 右侧：录入与提交区 */}
        <div className="flex-1 flex flex-col p-6 bg-white overflow-hidden">
          
          <div className="flex items-center mb-6 gap-4">
            <div className="flex flex-col flex-1 min-w-0">
               <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">责任厨师</span>
               <input 
                 value={chefName}
                 onChange={e => setChefName(e.target.value)}
                 className="text-lg font-black text-slate-800 outline-none bg-transparent border-b-2 border-transparent focus:border-blue-500 transition-all truncate"
               />
            </div>
            <div className="flex space-x-3 flex-shrink-0">
              <button 
                onClick={handleStartCamera}
                className="bg-slate-800 text-white px-6 py-4 rounded-2xl font-black text-sm shadow-lg active:scale-95 transition-all flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /></svg>
                <span>拍照录入</span>
              </button>
              <button 
                onClick={handleTakeSample}
                className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-sm shadow-lg active:scale-95 transition-all"
              >
                确认留样
              </button>
            </div>
          </div>

          <div className="flex-1 bg-slate-50 rounded-[2rem] flex flex-col overflow-hidden border border-slate-100">
            <div className="px-6 py-4 bg-white border-b border-slate-100 flex items-center justify-between">
              <span className="text-xs font-black text-slate-800 uppercase tracking-widest">待提交记录 ({sessionSamples.length})</span>
              <button onClick={onCancel} className="text-xs font-bold text-slate-400 hover:text-red-500">退出</button>
            </div>
            
            <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-3">
              {sessionSamples.length > 0 ? (
                sessionSamples.map((s) => (
                  <div key={s.id} className="bg-white p-4 rounded-2xl flex items-center justify-between border border-slate-100 shadow-sm animate-in slide-in-from-right-4">
                    <div className="flex items-center space-x-4">
                      <img src={s.photoUrl} className="w-12 h-12 rounded-xl object-cover" alt="sample" />
                      <div>
                        <p className="text-base font-black text-slate-800">{s.dishName}</p>
                        <p className="text-xs font-mono text-blue-600 font-bold">{s.weight}g</p>
                      </div>
                    </div>
                    <button onClick={() => removeSample(s.id)} className="p-2 text-slate-200 hover:text-red-500 transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                ))
              ) : (
                <div className="h-full flex items-center justify-center text-slate-300 italic text-sm">
                  等待录入数据...
                </div>
              )}
            </div>

            <div className="p-6 bg-white border-t border-slate-100">
               <button 
                onClick={handleFinalSubmit}
                disabled={sessionSamples.length === 0}
                className={`w-full py-5 rounded-full font-black text-xl flex items-center justify-center space-x-3 transition-all ${
                  sessionSamples.length === 0 
                  ? 'bg-slate-100 text-slate-300' 
                  : 'bg-blue-600 text-white shadow-xl shadow-blue-100 active:scale-95'
                }`}
               >
                 <span>提交保存</span>
                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
               </button>
            </div>
          </div>
        </div>
      </div>

      {/* 手工加菜简单弹窗 */}
      {showAddDishModal && (
        <div className="fixed inset-0 z-[1100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-200">
           <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden p-8 space-y-6 animate-in zoom-in-95 duration-200">
              <h3 className="text-xl font-black text-slate-800 text-center">增加菜品名称</h3>
              <input 
                autoFocus
                value={newDishName}
                onChange={e => setNewDishName(e.target.value)}
                placeholder="例如：西红柿炒鸡蛋"
                className="w-full bg-slate-50 border border-slate-200 px-5 py-4 rounded-2xl text-lg font-bold outline-none focus:border-blue-500 transition-all"
                onKeyDown={e => e.key === 'Enter' && handleAddManualDish()}
              />
              <div className="flex space-x-3">
                <button 
                  onClick={() => { setShowAddDishModal(false); setNewDishName(''); }}
                  className="flex-1 py-4 rounded-xl bg-slate-100 text-slate-500 font-bold active:scale-95 transition-all"
                >
                  取消
                </button>
                <button 
                  onClick={handleAddManualDish}
                  className="flex-1 py-4 rounded-xl bg-blue-600 text-white font-bold active:scale-95 shadow-lg shadow-blue-100 transition-all"
                >
                  确认增加
                </button>
              </div>
           </div>
        </div>
      )}

      {/* 相机预览蒙层 */}
      {isCapturing && (
        <div className="fixed inset-0 z-[1000] bg-black flex flex-col">
           <div className="flex-1 relative">
              <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                 <div className="w-[350px] h-[350px] border-2 border-white/40 rounded-[3rem] shadow-[0_0_0_9999px_rgba(0,0,0,0.6)]"></div>
              </div>
           </div>
           <div className="bg-black h-36 flex items-center justify-around">
              <button onClick={() => { if (videoRef.current?.srcObject) (videoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop()); setIsCapturing(false); }} className="text-white/40 text-lg font-bold">取消</button>
              <button onClick={() => { handleTakeSample(); setIsCapturing(false); if (videoRef.current?.srcObject) (videoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop()); }} className="w-20 h-20 bg-white rounded-full border-4 border-slate-800 active:scale-90 transition-all"></button>
              <div className="w-12"></div>
           </div>
        </div>
      )}
    </div>
  );
};

export default SamplingView;
